/* app/providers/ChatProvider.jsx — РЕАЛЬНОЕ состояние раздела Ассистент (боевой бекенд).
   Контракт значения (сайдбар + ChatShell):
   {chats, loading, error, forbidden, reload, search, setSearch, msgs,
    isBusy(cid), streamingIdFor(cid),  // ПОЧАТОВЫЙ стрим — busy/стрим-id по cid
    openChat, newChat, renameChat, shareChat, deleteChat, send, spawnChat, cancel(cid), regenerate, ensureMessages}.
   chat-2 добавил в value: {replaceDraft, registerDraftLive, unregisterDraftLive,
    commitPendingChange, cancelPendingChange, restorePendingChange, recomputeDraft, commitDraft}.

   Список → GET /api/chats/; сообщения → GET /api/chats/<id>/ (lazy, по открытию);
   отправка → POST /api/chats/<id>/messages/stream/ (fetch + ReadableStream + frameRouter).
   ВАЖНО: id чатов нормализуем в СТРОКУ — ChatShell сравнивает c.id===chatId (param из URL — строка).

   chat-1: delta/user/tool_call/sources/done/error. chat-2 (этот проход) — структурные кадры.
   draft_entity: metadata.draft_entities[] (редактируемый inline-черновик).
   confirm_action: metadata.pending_changes[] (карточка «было/станет»).
   partial-кадры: metadata.canvas (progressive-спека, ready на done через ttk_id/menu_id/report_id).
   navigate: роутер Кухни/отчётов; editor_action: best-effort navigate.
   specialist_start/end и route_thinking: лента tool-progress. Логика зеркалит
   frontend/pages/workspace/Workspace.jsx (onDraftEntity/onConfirmAction/onNavigate). */

import React from 'react';
import { useAuth } from './AuthProvider.jsx';
import { useConfirm } from './ConfirmProvider.jsx';
import { useToast } from './ToastProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import { RR } from '../../lib/router.jsx';
import * as chatsApi from '../../lib/api/chats.js';
import { QUIET_POLL } from '../../lib/api/client.js';
import {
  FRESH_MS, REFRESH_MS, broadcastUnread, clearFresh, clearFreshAll, mergeServerFlags, parseUnreadEvent, patchChat,
  unreadCount,
} from '../../lib/chat/unread.js';
// не фасад lib/api/index.js: провайдер живёт в оболочке (главный чанк), а фасад тянет слой «Продукции»
import * as api from '../../lib/api/chatActions.js';
import { parse as parsePartialJson, Allow as PJAllow } from 'partial-json';
import { adaptConfirmAction, adaptDraftEntity, partialCanvasPreview } from '../../lib/adapters/chats.js';
import { unwrap } from '../../lib/api/unwrap.js';
import { aiClosedDetail, isAiClosed } from '../../lib/aiAccess.js';
import { reportHref } from '../../lib/reportsSection.js';
import { ASSISTANT_CLOSED_TEXT, canUseAssistant } from '../../lib/assistantAccess.js';
// Право правки «Продукции» (pos-base-rework T9): статус интеграции читаем тем же путём, что
// адаптер «Продукции» (integrationsReal.get = unwrap(getIntegration)), — у чата своего
// контекста интеграции нет. Именованный импорт, а не объект адаптера: провайдер живёт в
// оболочке (главный чанк), а объект integrationsReal утащил бы туда весь слой API интеграций
// (+9,5 КБ; сторож — app/lazyScreens.test.js и замер сборки).
import { getIntegration } from '../../lib/api/integrations.js';
import { canEditKitchenFromQuery, kitchenAccessOf, kitchenEditForbiddenDetail } from '../../lib/chat/kitchenEditRight.js';

const TREE_QUOTA_MSG = 'Лимит запросов тарифа исчерпан. Повысьте тариф или попробуйте позже.';

  const { createContext, useContext, useState, useEffect, useRef, useCallback } = React;
  const { useNavigate } = RR;
  const { useFakeQuery } = hooks;

  const ChatContext = createContext(null);

  const asArray = (d) => (Array.isArray(d) ? d : (d && d.results) || []);

  // chat-2: тип канваса для structured *_partial по skill-слагу чата (нет report_type в кадре).
  const SKILL_CANVAS = {
    'ttk-generate': 'ttk', 'menu-design': 'menu',
    'foodcost-optimize': 'foodcost', 'abc-analyze': 'abc', 'inventory-analyze': 'inventory',
  };
  const partialCanvasType = (skillSlug, kind) => SKILL_CANVAS[skillSlug] || (
    kind === 'ttk' ? 'ttk' : kind === 'menu' ? 'menu' : kind
  );
  // Тип канваса отчёта: приоритет — metadata.report_type (оркестратор и skill-путь
  // кладут его при материализации), затем skill_slug чата. В свободном чате slug
  // пуст — раньше выходил несуществующий тип 'report' и канвас не рисовался.
  const REPORT_CANVAS = {
    foodcost: 'foodcost', abc: 'abc', inventory: 'inventory',
    'foodcost-optimize': 'foodcost', 'abc-analyze': 'abc', 'inventory-analyze': 'inventory',
  };
  const reportCanvasType = (reportType, skillSlug) => (
    REPORT_CANVAS[reportType] || SKILL_CANVAS[skillSlug] || null
  );

  function normalizeChat(c) {
    return {
      id: String(c.id),
      title: c.title || 'Новый диалог',
      visibility: c.visibility || 'private',
      mine: c.mine !== false,
      can_manage: c.can_manage !== false,
      skill_slug: c.skill_slug || null,
      metadata: c.metadata || {},
      author: c.author || null,
      updated_at: c.updated_at || c.created_at || null,
      // #503: признак есть только в списке; деталь его не несёт — не затираем им флаг строки
      ...(c.has_new_reply !== undefined ? { has_new_reply: !!c.has_new_reply } : {}),
      ...(c.last_reply_at !== undefined ? { last_reply_at: c.last_reply_at || null } : {}),
    };
  }

  function normalizeMessage(m) {
    return {
      id: 'm' + m.id,
      role: m.role,
      content: m.content || '',
      time: m.created_at || m.time || null,
      streaming: false,
      attachments: m.attachments || [],
      metadata: m.metadata || {},
      toolCalls: [],
    };
  }

  // F40 W5: снимок джобы сборки дерева в форме провайдера. stopReason — почему
  // бэк остановил сборку ('subscription_expired' — доступ закрыт, '' — ручная
  // «Стоп»; старый бэк ключа не шлёт → ''); waiting — сколько узлов ждут
  // достройки (их и вернёт resume после оплаты).
  function treeSnapshot(st) {
    const nodes = (st && st.nodes) || [];
    return {
      jobId: (st && st.job_id) || null,
      rootKind: (st && st.root_entity_kind) || null,
      rootId: (st && st.root_entity_id) || null,
      status: st ? st.status : 'idle',
      active: !!(st && (st.active || st.status === 'running' || st.status === 'pending')),
      total: (st && st.total) || 0, done: (st && st.done) || 0, failed: (st && st.failed) || 0,
      truncated: (st && st.truncated_count) || 0,
      failedTitles: nodes.filter((n) => n.status === 'failed').map((n) => n.title).filter(Boolean),
      stopReason: (st && st.stop_reason) || '',
      waiting: nodes.filter((n) => n.status === 'waiting').length,
    };
  }
  // Ключ корня (kind:id) — сверка «чей снимок в слоте диалога».
  const treeRootKey = (kind, id) => `${kind}:${id}`;

  async function listChatsAdapted() {
    const data = await unwrap(chatsApi.listChats());
    return asArray(data).map(normalizeChat);
  }

  // Карта прав «Продукции» текущего юзера (GET /api/integrations/ → access) или null.
  // Только у бизнес-пространства: в личном владелец всегда может, запрос не нужен. Любой отказ
  // (статус без карты, 403/500, сеть) → null = право открыто: решает сервер (403
  // kitchen_edit_forbidden покажем его detail), здесь лишь не предлагаем лишнего.
  async function loadKitchenAccess(workspace) {
    if (!workspace || workspace.kind !== 'business') return null;
    try {
      return kitchenAccessOf(await unwrap(getIntegration(workspace.id)));
    } catch {
      return null;
    }
  }

  function ChatProvider({ children }) {
    const { activeWorkspace } = useAuth();
    const navigate = useNavigate();
    const toast = useToast();
    const confirm = useConfirm();
    const wsId = activeWorkspace ? activeWorkspace.id : 'none';

    // список диалогов (loading/empty/error/403)
    const q = useFakeQuery('chats:' + wsId, () => listChatsAdapted(), [wsId]);
    // Право правки «Продукции» (kitchen.edit, pos-base-rework T9): карта access статуса
    // интеграции — раз на пространство, перечитывается по возврату во вкладку (администратор
    // мог поменять роль). Нет карты → открыто; явное false → ChatShell прячет плитки
    // «Сделать ТТК»/«Новое меню», карточки черновиков и канвасы правок — кнопки «Утвердить».
    const accessQ = useFakeQuery('chat:kitchen-access:' + wsId, () => loadKitchenAccess(activeWorkspace), [wsId], { refetchOnFocus: true });
    // смена пространства: карта прежнего ключа не применяется, пока не пришла новая (S10)
    const canEditKitchen = canEditKitchenFromQuery(accessQ);
    const [chats, setChats] = useState(null);
    const seeded = useRef(null);
    useEffect(() => {
      if (q.data && seeded.current !== wsId) {
        seeded.current = wsId;
        setChats(q.data);
      }
    }, [q.data, wsId]);
    useEffect(() => {
      seeded.current = null; setChats(null); setMsgs({}); loadedRef.current = {}; liveDraftsRef.current.clear();
      setListTab('private');
      // F40: гасим polling-петли сборки дерева прошлого пространства.
      Object.values(treeTimersRef.current).forEach((t) => clearTimeout(t));
      treeTimersRef.current = {}; treeOwnerRef.current = {}; setTreeJobs({});
      // F38: гасим reconnect-петли ходов прошлого пространства.
      Object.values(turnTimersRef.current).forEach((t) => clearTimeout(t));
      turnTimersRef.current = {}; setTurns({});
      // F41: квота считается per-workspace — сбрасываем анти-спам предупреждения,
      // иначе предупреждение нового пространства с тем же остатком проглотится.
      lowQuotaWarnedRef.current = null;
    }, [wsId]);

    // сообщения по чатам (lazy)
    const [msgs, setMsgs] = useState({});
    const msgsRef = useRef({});
    useEffect(() => { msgsRef.current = msgs; }, [msgs]);
    const loadedRef = useRef({}); // {cid:true} — уже загружены/созданы (не перезатирать с сервера)

    const [search, setSearch] = useState('');
    // Вкладка списка диалогов «Личные/Общие» (Business). Живёт в провайдере (не в
    // SidebarChats), чтобы shareChat переключал её вслед за перенесённым чатом и
    // чтобы сайдбар с мобильным drawer'ом показывали одну и ту же вкладку.
    const [listTab, setListTab] = useState('private');
    // ПОЧАТОВЫЙ стрим (независимые диалоги). `streaming` — карта cid→id ассистент-
    // сообщения (наличие ключа = этот чат стримит = busy для него); controllersRef
    // — карта cid→abort-замыкание. Раньше это были глобальные скаляры, и «Стоп» в
    // одном чате гасил ВСЕ диалоги. Теперь каждый чат стримится и останавливается
    // независимо.
    const [streaming, setStreaming] = useState({});
    const controllersRef = useRef({});
    // Синхронный per-chat замок отправки (Set из cid): `streaming` — React-стейт,
    // он выставляется внутри runStream (после await createRealChat), поэтому два
    // быстрых сабмита в ОДИН чат оба читают пусто и оба постят → дубль user-
    // сообщения (баг чата 284, msg 729≡730). Set переключается СИНХРОННО до await.
    const sendingRef = useRef(new Set());
    useEffect(() => () => { Object.values(controllersRef.current).forEach((fn) => fn && fn()); }, []);

    // F40 W5: фоновая сборка ДЕРЕВА детей утверждённой сущности (меню→блюда→
    // п/ф→сырьё). Процесс ЖИВЁТ НА БЕКЕ (TreeBuildJob) — refresh клиента его не
    // рвёт. Провайдер поллит статус по cid и держит чат «занятым», пока джоба
    // активна (кнопка «Стоп» композера гасит именно её). `treeJobs`:
    // cid → {jobId, rootKind, rootId, status, active, total, done, failed,
    // failedTitles, stopReason, waiting}. Ключ по cid → один диалог
    // собирает одно дерево за раз.
    const [treeJobs, setTreeJobs] = useState({});
    const treeJobsRef = useRef({});
    const treeTimersRef = useRef({});  // cid → setTimeout id polling-петли
    // cid → 'kind:id' корня, чей снимок СЕЙЧАС в слоте. Синхронно (не через
    // эффект): эффект карточки срабатывает раньше эффекта провайдера, и
    // treeJobsRef в этот момент ещё старый. По нему clearTreeFill не трогает
    // чужой корень.
    const treeOwnerRef = useRef({});
    useEffect(() => { treeJobsRef.current = treeJobs; }, [treeJobs]);
    useEffect(() => () => { Object.values(treeTimersRef.current).forEach((t) => clearTimeout(t)); }, []);

    // F38: серверные «ходы» чата (ChatTurn) живут на беке и переживают refresh.
    // На открытии/перезагрузке чата провайдер проверяет, не идёт ли ход (poll
    // /turn/), и пока идёт — держит чат «занятым» (спиннер-пузырь + «Стоп»),
    // а на финале рефетчит ленту (всплывает готовый ответ из БД). НОРМАЛЬНЫЙ
    // live-send по-прежнему идёт через runStream (SSE POST) — здесь только
    // reconnect-ветка. `turns`: cid → {active, status, error}.
    const [turns, setTurns] = useState({});
    const turnsRef = useRef({});
    const turnTimersRef = useRef({});
    useEffect(() => { turnsRef.current = turns; }, [turns]);
    useEffect(() => () => { Object.values(turnTimersRef.current).forEach((t) => clearTimeout(t)); }, []);

    // chat-entities-edit: живое состояние несохранённых inline-черновиков
    // (proposal_id → {proposal_id, kind, title, payload, status}). Карточки регистрируют
    // сюда свой текущий form; на отправке снимок уходит в draft_context (бэк подмешивает в
    // промпт → модель правит черновик). Ref (не state) — снимок нужен лишь в момент send.
    const liveDraftsRef = useRef(new Map());
    // F41: последний остаток сообщений, о котором предупредили (анти-спам тостов).
    const lowQuotaWarnedRef = useRef(null);
    const registerDraftLive = useCallback((proposalId, snapshot) => {
      if (proposalId) liveDraftsRef.current.set(proposalId, snapshot);
    }, []);
    const unregisterDraftLive = useCallback((proposalId) => {
      if (proposalId) liveDraftsRef.current.delete(proposalId);
    }, []);

    const setChatMsgs = (cid, updater) => setMsgs((prev) => ({ ...prev, [cid]: updater(prev[cid] || []) }));

    // chat-entities: слить патч (committed/dismissed/draft + committed_target) в нужный
    // черновик metadata.draft_entities по proposal_id — чтобы карточка перерисовалась
    // (зеркалит Workspace.onReplaceDraft). Ищем по всем сообщениям всех чатов.
    const replaceDraft = useCallback((proposalId, patch) => {
      setMsgs((all) => {
        const out = {};
        for (const cid of Object.keys(all)) {
          out[cid] = (all[cid] || []).map((m) => {
            const list = m.metadata && m.metadata.draft_entities;
            if (!Array.isArray(list) || !list.some((d) => d.proposal_id === proposalId)) return m;
            return {
              ...m,
              metadata: {
                ...m.metadata,
                draft_entities: list.map((d) => (d.proposal_id === proposalId ? { ...d, ...patch } : d)),
              },
            };
          });
        }
        return out;
      });
    }, []);

    // Подгрузить сообщения диалога по открытию (один раз). Не затирает активную/созданную ленту.
    const ensureMessages = useCallback(async (rawId) => {
      if (rawId == null) return;
      const cid = String(rawId);
      if (loadedRef.current[cid]) return;
      loadedRef.current[cid] = true;
      const r = await chatsApi.getChat(cid);
      if (r.ok && r.data) {
        // Канвас-спека из истории: бэк кладёт ttk_id/menu_id/report_id в metadata
        // assistant-сообщения при structured ready. Тип отчёта берём из skill_slug чата
        // (в кадре report_type нет) — зеркалит Workspace.selectChat. Канонизируем в
        // metadata.canvas, чтобы canvasSpecFromMeta/CanvasHost отрисовали готовый канвас.
        const slug = r.data.skill_slug || '';
        const list = (r.data.messages || []).map((m) => {
          const msg = normalizeMessage(m);
          const md = msg.metadata || {};
          if (!md.canvas) {
            if (md.ttk_id != null) md.canvas = { type: 'ttk', id: md.ttk_id };
            else if (md.menu_id != null) md.canvas = { type: 'menu', id: md.menu_id };
            else if (md.report_id != null) {
              const t = reportCanvasType(md.report_type, slug);
              if (t) md.canvas = { type: t, id: md.report_id };
            }
          }
          return msg;
        });
        setMsgs((m) => ({ ...m, [cid]: list }));
        // подтянем актуальные поля чата (title/visibility), если он уже в списке
        setChats((l) => (l || []).map((c) => (c.id === cid ? { ...c, ...normalizeChat(r.data), id: cid } : c)));
      } else {
        loadedRef.current[cid] = false; // дать повторить
      }
    }, []);

    // ── #503: «пришёл новый ответ» ───────────────────────────────────────────────
    // Какой диалог на экране — сообщает ChatShell (setViewing). Ответ пришёл, а человек не
    // смотрит (другой чат/раздел или вкладка скрыта) — строка списка получает флаг и вспышку;
    // смотрит — сразу «прочитано» на сервере. Соседние вкладки — storage-событием; ответы с других
    // устройств и от других участников — флагами списка, перечитанного тихо по возврату во вкладку.
    const viewingRef = useRef(null);
    const [viewing, setViewingState] = useState(null);
    const chatsRef = useRef(null);
    useEffect(() => { chatsRef.current = chats; }, [chats]);
    const wsRef = useRef(wsId);
    wsRef.current = wsId;
    const openedRef = useRef(null);          // 'ws:cid' — открытие, по которому «прочитано» уже ушло
    const refreshedAtRef = useRef(0);
    const onScreen = (scid) => viewingRef.current === scid
      && (typeof document === 'undefined' || document.visibilityState !== 'hidden');
    // id последнего ответа сервера, который эта вкладка показала ('m<id>'); ленты нет — null
    const lastShownId = (scid) => {
      const arr = msgsRef.current[scid];
      if (!Array.isArray(arr)) return null;
      for (let i = arr.length - 1; i >= 0; i -= 1) {
        const m = /^m(\d+)$/.exec((arr[i] && arr[i].id) || '');
        if (m) return Number(m[1]);
      }
      return null;
    };
    const markRead = useCallback((cid, upto) => {
      const scid = String(cid);
      setChats((l) => patchChat(l, scid, { has_new_reply: false, fresh: 0 }));
      chatsApi.markChatRead(scid, upto).catch(() => {});
      broadcastUnread('read', wsRef.current, scid);
    }, []);
    const flagReply = useCallback((scid, at) => {
      const stamp = Date.now();
      setChats((l) => patchChat(l, scid, { has_new_reply: true, fresh: stamp, last_reply_at: at || new Date(stamp).toISOString() }));
      setTimeout(() => setChats((l) => clearFresh(l, scid, stamp)), FRESH_MS);
    }, []);
    // лента уже в памяти, а на сервере есть ответ, которого вкладка не показала, — сначала дотянуть
    // ленту, потом «прочитано» (иначе прочитанным стал бы непоказанный ответ)
    const refetchThenRead = useCallback((scid) => {
      setChats((l) => patchChat(l, scid, { has_new_reply: false, fresh: 0 }));
      loadedRef.current[scid] = false;
      Promise.resolve(ensureMessages(scid)).then(() => markRead(scid), () => markRead(scid));
    }, [ensureMessages, markRead]);
    // ws — пространство, в котором ход НАЧАЛСЯ: вкладка могла уже перейти в другое (S10) — тогда
    // только сообщить соседним вкладкам того пространства
    const noteReply = useCallback((cid, serverMsgId, ws) => {
      const scid = String(cid);
      const turnWs = String(ws == null ? wsRef.current : ws);
      if (turnWs !== String(wsRef.current)) { broadcastUnread('reply', turnWs, scid); return; }
      if (onScreen(scid)) { markRead(scid, serverMsgId); return; }
      flagReply(scid);
      broadcastUnread('reply', turnWs, scid);
    }, [markRead, flagReply]);
    const noteReplyRef = useRef(noteReply);
    noteReplyRef.current = noteReply;
    const setViewing = useCallback((cid) => {
      const v = cid == null ? null : String(cid);
      viewingRef.current = v;
      openedRef.current = null;
      setViewingState(v);
    }, []);
    // Открыл диалог (вкладка видна, диалог в списке ЭТОГО пространства) — «прочитано» один раз на
    // открытие, до показанного; это же заводит отметку на будущее. Флаг, поставленный, пока диалог
    // на экране (список пришёл позже, соседняя вкладка, тихое перечитывание), — снять; лента уже в
    // памяти — сначала дотянуть её. Та же логика — по возврату во вкладку (открыт был скрытым).
    const settleOnScreen = useCallback(() => {
      const v = viewingRef.current;
      if (!v || !onScreen(v)) return;
      const c = (chatsRef.current || []).find((x) => x.id === v);
      if (!c) return;
      const key = `${wsRef.current}:${v}`;
      if (openedRef.current === key && !c.has_new_reply) return;
      openedRef.current = key;
      const shown = lastShownId(v);
      if (c.has_new_reply && shown != null) { refetchThenRead(v); return; }
      markRead(v, shown == null ? undefined : shown);
    }, [markRead, refetchThenRead]);
    useEffect(() => { settleOnScreen(); }, [viewing, chats, wsId, settleOnScreen]);
    // Тихо перечитать флаги списка (не чаще раза в REFRESH_MS): ответы с других устройств и от
    // других участников общего чата. Только чтение — вне счёта запросов в полёте (QUIET_POLL).
    const refreshFlags = useCallback(() => {
      const ws = String(wsRef.current);
      if (Date.now() - refreshedAtRef.current < REFRESH_MS) return;
      refreshedAtRef.current = Date.now();
      chatsApi.listChats(QUIET_POLL).then((r) => {
        if (!r || !r.ok || !Array.isArray(r.data) || String(wsRef.current) !== ws) return;
        const stamp = Date.now();
        setChats((l) => mergeServerFlags(l, r.data, stamp));
        setTimeout(() => setChats((l) => clearFreshAll(l, stamp)), FRESH_MS);
      }).catch(() => {});
    }, []);
    // вернулся во вкладку — ответ на экране прочитан; флаги остальных — с сервера
    useEffect(() => {
      if (typeof document === 'undefined') return undefined;
      const onVis = () => {
        if (document.visibilityState === 'hidden') return;
        settleOnScreen();
        refreshFlags();
      };
      document.addEventListener('visibilitychange', onVis);
      return () => document.removeEventListener('visibilitychange', onVis);
    }, [settleOnScreen, refreshFlags]);
    // соседняя вкладка: прочитала — снять флаг; получила ответ — флаг (или, если этот диалог у нас
    // на экране, дотянуть ленту и отметить прочитанным). Чужое пространство — мимо (S10).
    useEffect(() => {
      if (typeof window === 'undefined' || !window.addEventListener) return undefined;
      const onStorage = (e) => {
        const ev = parseUnreadEvent(e.key, e.newValue);
        if (!ev || ev.ws !== String(wsRef.current)) return;
        if (ev.kind === 'read') { setChats((l) => patchChat(l, ev.chat, { has_new_reply: false, fresh: 0 })); return; }
        if (onScreen(ev.chat)) { refetchThenRead(ev.chat); return; }
        flagReply(ev.chat);
      };
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    }, [refetchThenRead, flagReply]);

    // navigate по kind/id (зеркалит Workspace NAV_MAP + отчёты): открывает сущность в Кухне.
    const navByKind = useCallback((kind, id) => {
      if (id == null) return;
      if (kind === 'ttk') navigate(`/kitchen?tab=recipes&edit=ttk-${id}`);
      else if (kind === 'card') navigate(`/kitchen?tab=ingredients&edit=card-${id}`);
      else if (kind === 'menu') navigate(`/kitchen?tab=menus&edit=menu-${id}`);
      else if (kind === 'report' && reportHref(id)) navigate(reportHref(id));
    }, [navigate]);

    // Запустить реальный стрим в указанное ассистент-сообщение.
    // skillSlug — слаг скилла чата (для маппинга *_partial → тип канваса).
    const runStream = useCallback((cid, aId, userMsgId, text, opts = {}, skillSlug = '', mode = 'send', serverMsgId = null) => {
      const controller = new AbortController();
      const scid = String(cid);
      const turnWs = wsRef.current;          // #503: пространство, в котором начался ход
      controllersRef.current[scid] = () => controller.abort();
      setStreaming((s) => ({ ...s, [scid]: aId }));
      const updA = (fn) => setChatMsgs(cid, (arr) => arr.map((m) => (m.id === aId ? fn(m) : m)));
      const updU = (fn) => setChatMsgs(cid, (arr) => arr.map((m) => (m.id === userMsgId ? fn(m) : m)));
      const finish = () => {
        delete controllersRef.current[scid];
        setStreaming((s) => { const n = { ...s }; delete n[scid]; return n; });
      };

      // chat-2: прогрессивный предпросмотр канваса. Бэк стримит СЫРЫЕ фрагменты JSON
      // структурного ответа (ttk_partial/menu_partial/<report>_partial). Копим их по типу,
      // partial-парсим (Allow.ALL — терпит обрезанный JSON) и для ttk/menu мапим в preview
      // (имена/разделы). Отчёты — только скелет (числа мид-стрим бессмысленны, инвариант
      // «LLM извлекает — код считает»). На done канвас сменится на {type,id} (реальная сущность).
      const partialAcc = { ttk: '', menu: '', foodcost: '', abc: '', inventory: '' };
      const onPartial = (kind, frag) => {
        const type = partialCanvasType(skillSlug, kind);
        if (!type) return;
        let preview = null;
        if (kind === 'ttk' || kind === 'menu') {
          partialAcc[kind] += (frag || '');
          try { preview = partialCanvasPreview(kind, parsePartialJson(partialAcc[kind], PJAllow.ALL)); }
          catch { preview = null; }
        }
        updA((m) => {
          if (m.metadata && m.metadata.canvas && m.metadata.canvas.id != null) return m; // ready — не трогаем
          const canvas = preview ? { type, partial: preview } : { type, progressive: true };
          return { ...m, metadata: { ...m.metadata, canvas } };
        });
      };
      // chat-entities: propose_change → крепим карточку подтверждения к metadata.pending_changes.
      let pendingNavigate = null;

      // Общий handler-bag кадров — одинаков для send и regenerate.
      const handlers = {
        signal: controller.signal,
        onDelta: (t) => updA((m) => ({ ...m, content: m.content + t })),
        onToolCallStart: (call_id, name, label) => updA((m) => ({ ...m, toolCalls: [...m.toolCalls, { call_id, name, status: 'running', summary: label || '' }] })),
        onToolCallEnd: (call_id, ok, summary) => updA((m) => ({ ...m, toolCalls: m.toolCalls.map((tc) => (tc.call_id === call_id ? { ...tc, status: ok ? 'done' : 'error', summary: summary || tc.summary } : tc)) })),
        onSources: (items) => updA((m) => ({ ...m, metadata: { ...m.metadata, sources: items } })),
        // F36 W9: хоп оркестратора — та же лента toolCalls[], помечаем kind:'specialist'.
        onSpecialistStart: (e) => updA((m) => ({ ...m, toolCalls: [...m.toolCalls, { call_id: e.call_id, name: e.specialist, status: 'running', summary: e.label || '', kind: 'specialist' }] })),
        onSpecialistEnd: (e) => updA((m) => ({ ...m, toolCalls: m.toolCalls.map((tc) => (tc.call_id === e.call_id ? { ...tc, status: e.ok ? 'done' : 'error', summary: e.summary || tc.summary } : tc)) })),
        onRouteThinking: () => {}, // forward-compat: бэк пока не эмитит
        // *_partial: прогрессивный предпросмотр канваса (копим фрагменты JSON → partial-parse).
        onTtkPartial: (frag) => onPartial('ttk', frag),
        onMenuPartial: (frag) => onPartial('menu', frag),
        onReportPartial: (type, frag) => onPartial(type, frag),
        // navigate (open_entity): переход — на done (не рвём SSE-ридер).
        onNavigate: (kind, id) => { pendingNavigate = { kind, id }; },
        // editor_action (open_*_editor): prefill-гидрация редактора в прототипе слишком
        // инвазивна (черновик уже показывается через draft_entity) → best-effort навигация
        // в Кухню на done; здесь только запоминаем цель.
        onEditorAction: (editor) => {
          const map = { card: 'card', ingredient: 'card', ttk: 'ttk', recipe: 'ttk', menu: 'menu' };
          const kind = map[editor] || null;
          if (kind) pendingNavigate = pendingNavigate || { kind, id: null, editor: true };
        },
        // confirm_action: подготовленная правка → карточка «было→станет» в metadata.pending_changes.
        // Новая правка ВЫТЕСНЯЕТ прежние pending той же цели (бэк ставит им
        // EXPIRED + зеркалит в metadata) — гасим их и в живом состоянии, иначе
        // старый канвас до reload продолжал бы предлагать «Утвердить» (409).
        onConfirmAction: (e) => {
          const pc = adaptConfirmAction(e);
          setChatMsgs(cid, (arr) => arr.map((m) => {
            const list = m.metadata && m.metadata.pending_changes;
            if (!Array.isArray(list)) return m;
            let touched = false;
            const next = list.map((it) => {
              if (it && it.kind === pc.kind && it.target_id === pc.target_id
                  && it.id !== pc.id && (!it.status || it.status === 'pending')) {
                touched = true;
                return { ...it, status: 'stale' };
              }
              return it;
            });
            return touched ? { ...m, metadata: { ...m.metadata, pending_changes: next } } : m;
          }));
          updA((m) => {
            const prev = Array.isArray(m.metadata && m.metadata.pending_changes) ? m.metadata.pending_changes : [];
            return { ...m, metadata: { ...m.metadata, pending_changes: [...prev, pc] } };
          });
        },
        // draft_entity (F40 W4): редактируемый inline-черновик-ВЕРСИЯ. Правка = новая
        // версия линии (version_group_id) новым кадром; старые версии НЕ удаляем —
        // MessageList собирает версии линии across-messages в галерею и ВЫВОДИТ
        // superseded из max(version_n). Здесь лишь дедупим точный повтор кадра той
        // же версии (по proposal_id) и крепим новую версию к стрим-пузырю (aId).
        onDraftEntity: (e) => {
          const draft = adaptDraftEntity(e);
          setChatMsgs(cid, (arr) => {
            const deduped = arr.map((m) => {
              const list = m.metadata && m.metadata.draft_entities;
              if (!Array.isArray(list) || !list.some((d) => d.proposal_id === draft.proposal_id)) return m;
              return { ...m, metadata: { ...m.metadata, draft_entities: list.filter((d) => d.proposal_id !== draft.proposal_id) } };
            });
            return deduped.map((m) => {
              if (m.id !== aId) return m;
              const prev = Array.isArray(m.metadata && m.metadata.draft_entities) ? m.metadata.draft_entities : [];
              return { ...m, metadata: { ...m.metadata, draft_entities: [...prev, draft] } };
            });
          });
        },
        onDone: (e) => {
          const meta = (e && e.message && e.message.metadata) || {};
          const serverMsgId = e && e.message && e.message.id;
          updA((m) => {
            // metadata сервера — источник истины (draft_entities/pending_changes/sources + ttk_id/…).
            // Мёржим: серверная meta поверх накопленной локально, но canvas ready-спеку из id ставим явно.
            // Серверная draft_entities на done — финальная сводка; но если локально пользователь уже
            // правил/коммитил блок (через replaceDraft), серверная версия его перетёрла бы. Поэтому
            // сохраняем ЛОКАЛЬНЫЕ draft_entities/pending_changes (они уже отражают кадры стрима + правки).
            const localDrafts = m.metadata && m.metadata.draft_entities;
            const localPending = m.metadata && m.metadata.pending_changes;
            const merged = { ...m.metadata, ...meta };
            if (Array.isArray(localDrafts) && localDrafts.length) merged.draft_entities = localDrafts;
            if (Array.isArray(localPending) && localPending.length) merged.pending_changes = localPending;
            let canvas = m.metadata && m.metadata.canvas;
            if (meta.ttk_id != null) canvas = { type: 'ttk', id: meta.ttk_id };
            else if (meta.menu_id != null) canvas = { type: 'menu', id: meta.menu_id };
            else if (meta.report_id != null) {
              const t = reportCanvasType(meta.report_type, skillSlug);
              if (t) canvas = { type: t, id: meta.report_id };
            }
            if (canvas) merged.canvas = canvas;
            // Принимаем серверный id сообщения (формат 'm'+id, как у истории) — нужен для
            // идемпотентного commitDraft (message_id-маркер). Без него commit всё равно работает.
            const nextId = serverMsgId != null ? 'm' + serverMsgId : m.id;
            return { ...m, id: nextId, streaming: false, content: (e && e.message && e.message.content) || m.content, metadata: merged };
          });
          // skill_slug — тоже с сервера: старый чат-отчёт после первого сообщения стал свободным
          // (№524) — перегенерация и повтор открываются сразу, без перезагрузки
          if (e && e.conversation) setChats((l) => (l || []).map((c) => (c.id === cid ? {
            ...c, title: e.conversation.title || c.title, updated_at: e.conversation.updated_at || c.updated_at,
            ...(e.conversation.skill_slug !== undefined ? { skill_slug: e.conversation.skill_slug || null } : {}),
          } : c)));
          if (pendingNavigate) navByKind(pendingNavigate.kind, pendingNavigate.id);
          // #503: человек не смотрит на этот диалог — «пришёл новый ответ» в списке
          noteReplyRef.current(scid, serverMsgId, turnWs);
          // Живой счётчик расхода: ход засчитан в квоту — сигналим переключателю
          // пространства перезапросить /me/ (счётчик меняется без перезагрузки).
          try { window.dispatchEvent(new CustomEvent('quota:changed')); } catch { /* noop */ }
          // F41: предупреждение о низком остатке сообщений периода (порог ≤5;
          // без спама — на каждое значение остатка предупреждаем один раз).
          const rem = e && e.usage && e.usage.chat_messages_remaining;
          if (typeof rem === 'number' && rem <= 5 && lowQuotaWarnedRef.current !== rem) {
            lowQuotaWarnedRef.current = rem;
            toast.show(
              rem > 0
                ? `Осталось ${rem} сообщени${rem === 1 ? 'е' : rem < 5 ? 'я' : 'й'} в этом периоде`
                : 'Лимит сообщений периода исчерпан',
              rem > 0 ? 'accent' : 'danger',
            );
          }
          finish();
        },
        // F41: ошибка стрима — пузырь ошибки в ленте + тост и на ПЕРВОМ стриме
        // (раньше тост был только при регенерации, и сбой выглядел как «завис»).
        onError: (e) => {
          updA((m) => ({ ...m, streaming: false, metadata: { ...m.metadata, stream_error: (e && e.detail) || 'Ошибка генерации', stream_error_code: (e && e.code) || null } }));
          // Квота и окончание оплаты объясняются пузырём в ленте — тост поверх
          // него только дублировал бы то же самое красным.
          // 403 kitchen_edit_forbidden (ход в чат-скилл ТТК/меню без права правки,
          // pos-base-rework) — причина сервера нейтрально: это не сбой генерации.
          const forbidden = kitchenEditForbiddenDetail(e);
          if (forbidden) toast.show(forbidden, 'warning', 8000);
          else if (!['chat_quota_exceeded', 'subscription_expired', 'member_quota_exceeded',
            'assistant_forbidden'].includes(e && e.code)) {
            toast.show((e && e.detail) || 'Не удалось сгенерировать ответ', 'danger');
          }
          finish();
        },
      };

      // regenerate: стрим «на месте» в тот же aId-пузырь, без onUser/вложений —
      // бэк сам берёт предыдущий user-ход (удалив хвостовой assistant + pending).
      // send: добавляем вложения/draft_context/onUser (адопт серверного user-id).
      if (mode === 'regenerate') {
        const orig = opts.regenOriginal;
        const onRegenError = (e) => {
          // при сбое стрима возвращаем прежний ответ (бэк его тоже восстановил),
          // чтобы не оставить пустой пузырь; уведомляем тостом.
          if (orig) updA((m) => ({ ...m, content: orig.content, toolCalls: orig.toolCalls, metadata: orig.metadata, streaming: false }));
          else updA((m) => ({ ...m, streaming: false, metadata: { ...m.metadata, stream_error: (e && e.detail) || 'Не удалось перегенерировать' } }));
          // Закрытый доступ к модели — не сбой, а ожидание тарифа: тон как у
          // остальных поверхностей «нет доступа → нет модели».
          toast.show((e && e.detail) || 'Не удалось перегенерировать ответ', (isAiClosed(e) || kitchenEditForbiddenDetail(e)) ? 'warning' : 'danger');
        };
        return chatsApi.regenerateMessage(cid, serverMsgId, { ...handlers, draft_context: [], onError: onRegenError })
          .then(finish, finish); // страховка: стрим закрылся без done/error
      }
      // F41: повтор упавшего хода — стрим в существующий пузырь ошибки, без
      // нового user-сообщения (бэк берёт прежний user-ход failed-хода).
      if (mode === 'retry') {
        return chatsApi.retryTurnStream(cid, { ...handlers, draft_context: opts.draft_context || [] })
          .then(finish, finish);
      }
      return chatsApi.sendMessageStream(cid, text, {
        ...handlers,
        attachment_ids: (opts.attachment_ids || (opts.attachments || []).map((a) => a.id)).filter((x) => typeof x === 'number'),
        draft_context: opts.draft_context || [],
        onboarding: !!opts.onboarding,   // демо-онбординг: бэк не считает в квоту, метит чат
        onUser: (e) => { if (e && e.message) updU((m) => ({ ...m, id: 'm' + e.message.id, time: e.message.created_at || m.time })); },
      }).then(finish, finish); // страховка: стрим закрылся без done/error
    }, [navByKind]);

    // ── F40 W5: polling-петля статуса фоновой сборки дерева ─────────────────────
    const stopTreePoll = useCallback((scid) => {
      const t = treeTimersRef.current[scid];
      if (t) { clearTimeout(t); delete treeTimersRef.current[scid]; }
    }, []);

    // Записать снимок джобы в treeJobs[cid]; вернуть active?
    const applyTreeJob = useCallback((scid, st) => {
      const snap = treeSnapshot(st);
      treeOwnerRef.current[scid] = treeRootKey(snap.rootKind, snap.rootId);
      setTreeJobs((m) => ({ ...m, [scid]: snap }));
      return snap.active;
    }, []);

    const pollTreeFill = useCallback((scid, jobId) => {
      api.treeBuildStatus(jobId).then((st) => {
        const active = applyTreeJob(scid, st);
        // Живой счётчик расхода: блюда меню списывают квоту ПОСЛЕДОВАТЕЛЬНО по
        // мере сборки — на каждом тике сборки обновляем счётчик без перезагрузки.
        try { window.dispatchEvent(new CustomEvent('quota:changed')); } catch { /* noop */ }
        if (active) {
          treeTimersRef.current[scid] = setTimeout(() => pollTreeFill(scid, jobId), 1800);
        } else {
          stopTreePoll(scid);
          // готово/остановлено/ошибка → обновить реестр Кухни (новые ТТК/п/ф).
          window.dispatchEvent(new CustomEvent('kitchen:ttks-changed'));
        }
      }).catch(() => {
        // сетевой сбой polling — мягкий ретрай (джоба на беке продолжается).
        treeTimersRef.current[scid] = setTimeout(() => pollTreeFill(scid, jobId), 3000);
      });
    }, [applyTreeJob, stopTreePoll]);

    // Запустить polling-петлю один раз на чат. Сентинел в treeTimersRef ставится
    // СИНХРОННО — иначе старт-после-commit и resume-после-refresh оба кикнут петлю
    // (двойной polling). Реальный timer-id заменит сентинел после первого ответа.
    const beginTreePoll = useCallback((scid, jobId) => {
      if (treeTimersRef.current[scid]) return;
      treeTimersRef.current[scid] = true;
      pollTreeFill(scid, jobId);
    }, [pollTreeFill]);

    // Старт после утверждения: карточка передаёт начальный снимок tree_build_job
    // из ответа commit ({job_id, root_kind, root_id, status, total, done,
    // failed}). Если бэк уже вернул терминал (sync/edge) — не зацикливаемся.
    const startTreeFill = useCallback((cid, initial = {}) => {
      const scid = String(cid);
      const jobId = initial.job_id;
      if (jobId == null) return;
      stopTreePoll(scid);
      const snap = {
        job_id: jobId, root_entity_kind: initial.root_kind, root_entity_id: initial.root_id,
        status: initial.status, total: initial.total, done: initial.done, failed: initial.failed,
      };
      const terminal = initial.status && initial.status !== 'running' && initial.status !== 'pending';
      if (terminal) { applyTreeJob(scid, snap); window.dispatchEvent(new CustomEvent('kitchen:ttks-changed')); return; }
      applyTreeJob(scid, { ...snap, active: true });
      beginTreePoll(scid, jobId);
    }, [applyTreeJob, beginTreePoll, stopTreePoll]);

    // Восстановление после refresh: карточка committed-сущности зовёт это на
    // mount c (rootKind, rootId); активную джобу корня находит бэк (by-root,
    // protухший heartbeat он же и переспавнивает).
    // Промис → снимок джобы, если сборку остановил закрытый доступ (иначе null).
    const resumeTreeFill = useCallback((cid, rootKind, rootId) => {
      const scid = String(cid);
      if (rootKind == null || rootId == null) return Promise.resolve(null);
      // by-root зовём ВСЕГДА, даже если в диалоге уже идёт петля другого корня:
      // петли переживают переключение чатов, и перемонтированная карточка
      // остановленного доступом корня иначе теряла бы причину и «Достроить».
      return api.treeBuildByRoot(rootKind, rootId).then((st) => {
        if (st && (st.active || st.status === 'running' || st.status === 'pending')) {
          // В этом диалоге уже идёт петля (другого корня или пришла, пока ждали
          // ответ) — слот её: не затираем и вторую петлю не запускаем.
          if (treeTimersRef.current[scid]) return null;
          applyTreeJob(scid, st);
          beginTreePoll(scid, st.job_id);
          return null;
        }
        if (st && st.status === 'cancelled' && st.stop_reason === 'subscription_expired') {
          // Сборку остановил закрытый доступ: узлы ждут в waiting, после оплаты
          // resume её продолжит. Снимок терминальный — отдаём его карточке
          // (она держит «Достроить» и причину у себя), а в общий слот диалога
          // НЕ пишем: слот один на cid, и снимок корня B затёр бы живую запись
          // корня A, а очистка B погасила бы polling A.
          return treeSnapshot(st);
        }
        return null;
      }).catch(() => null);
    }, [applyTreeJob, beginTreePoll]);

    // Карточка сняла терминальный снимок → убрать запись (isBusy спадёт окончательно).
    // rootKind/rootId — чей снимок снимаем: если слот уже держит ДРУГОЙ корень,
    // это no-op (чужую запись и её polling не трогаем). Без корня — как раньше.
    const clearTreeFill = useCallback((cid, rootKind, rootId) => {
      const scid = String(cid);
      const scoped = rootKind != null && rootId != null;
      const key = scoped ? treeRootKey(rootKind, rootId) : null;
      const owner = treeOwnerRef.current[scid];
      if (scoped && owner && owner !== key) return;
      stopTreePoll(scid);
      delete treeOwnerRef.current[scid];
      setTreeJobs((m) => {
        const j = m[scid];
        if (!j) return m;
        if (scoped && treeRootKey(j.rootKind, j.rootId) !== key) return m;
        const n = { ...m }; delete n[scid]; return n;
      });
    }, [stopTreePoll]);

    // «Достроить»: failed-узлы → waiting, джоба переспавнивается; петля заново.
    // jobId — явный (карточка хранит терминальный снимок локально: запись в
    // treeJobs уже очищена clearTreeFill после финального тоста).
    // Промис → true только при запущенной сборке. ЛЮБОЙ отказ → false (карточка
    // оставляет кнопку и пометку) + тост: закрытый доступ (402
    // subscription_expired) — warning с detail бэкенда (после оплаты
    // «Достроить» сработает), квота — warning о лимите, прочее — danger.
    const treeActionFailed = useCallback((err) => {
      const closed = aiClosedDetail(err);
      if (closed) toast.show(closed, 'warning', 8000);
      else if (err && err.code === 'quota_exceeded') toast.show(TREE_QUOTA_MSG, 'warning');
      // №523: личный лимит участника / Аргус закрыт администратором — текст сервера, не «тариф»
      else if (err && (err.code === 'member_quota_exceeded' || err.code === 'assistant_forbidden')
        && typeof err.detail === 'string' && err.detail) toast.show(err.detail, 'warning', 8000);
      else toast.show((err && typeof err.detail === 'string' && err.detail) || 'Не удалось запустить сборку', 'danger');
      return false;
    }, [toast]);

    const resumeFailedTree = useCallback((cid, jobId) => {
      const scid = String(cid);
      const jid = jobId != null ? jobId : (treeJobsRef.current[scid] || {}).jobId;
      if (jid == null) return Promise.resolve(true);
      return api.treeBuildResume(jid).then((st) => {
        if (applyTreeJob(scid, st)) beginTreePoll(scid, jid);
        return true;
      }).catch(treeActionFailed);
    }, [applyTreeJob, beginTreePoll, treeActionFailed]);

    // «Достроить» срезанное потолком дерево: у done-джобы с truncated_count>0
    // resume бессилен (failed-узлов нет, недостающие узлы НЕ созданы) — нужен
    // НОВЫЙ прогон по корню (tree/start пересеет оставшиеся стабы; квота-гейт
    // на беке, идемпотентно по активной джобе).
    const restartTreeBuild = useCallback((cid, rootKind, rootId) => {
      const scid = String(cid);
      return api.treeBuildStart(rootKind, rootId, { chat_id: cid }).then((st) => {
        if (st && st.job_id && applyTreeJob(scid, st)) beginTreePoll(scid, st.job_id);
        return true;
      }).catch(treeActionFailed);
    }, [applyTreeJob, beginTreePoll, treeActionFailed]);

    // ── F38: reconnect к серверному ходу после refresh ──────────────────────────
    const stopTurnPoll = useCallback((scid) => {
      const t = turnTimersRef.current[scid];
      if (t) { clearTimeout(t); delete turnTimersRef.current[scid]; }
    }, []);

    // Спиннер-пузырь «думает…» в конце ленты, пока ход активен (self-healing:
    // если рефетч ленты его стёр — следующий тик добавит снова).
    const ensureTurnSpinner = useCallback((scid) => {
      setChatMsgs(scid, (arr) => {
        const tail = arr[arr.length - 1];
        if (tail && tail.role === 'assistant' && tail.streaming) return arr;
        return [...arr, {
          id: 'turn-spinner-' + scid, role: 'assistant', content: '',
          time: new Date().toISOString(), streaming: true,
          attachments: [], metadata: {}, toolCalls: [],
        }];
      });
    }, []);

    // F41: персистентный пузырь ошибки упавшего хода (ответа в БД нет — после
    // рефетча лента кончается user-сообщением). Кнопку «Повторить» рисует
    // MessageList по metadata.stream_error. Self-healing: id детерминирован,
    // повторная вставка дедупится.
    const ensureFailedBubble = useCallback((scid, st) => {
      setChatMsgs(scid, (arr) => {
        if (arr.some((m) => m.id === 'turn-failed-' + scid)) return arr;
        const tail = arr[arr.length - 1];
        if (!tail || tail.role !== 'user') return arr; // ответ уже есть — пузырь не нужен
        return [...arr, {
          id: 'turn-failed-' + scid, role: 'assistant', content: '',
          time: new Date().toISOString(), streaming: false,
          attachments: [], toolCalls: [],
          metadata: { stream_error: (st && st.error_detail) || 'Не удалось сгенерировать ответ.' },
        }];
      });
    }, []);

    // Одна итерация поллинга статуса хода: активен → спиннер + повтор; терминал →
    // стоп + рефетч ленты (готовый ответ из БД заменит спиннер), failed → тост +
    // пузырь ошибки с «Повторить» (F41).
    const pollTurn = useCallback((scid) => {
      chatsApi.getTurnStatus(scid).then((r) => {
        const st = r && r.ok ? r.data : null;
        const active = !!(st && (st.active || st.status === 'running' || st.status === 'pending'));
        setTurns((m) => ({ ...m, [scid]: { active, status: st ? st.status : 'idle', error: (st && st.error_detail) || '' } }));
        if (active) {
          ensureTurnSpinner(scid);
          turnTimersRef.current[scid] = setTimeout(() => pollTurn(scid), 1500);
        } else {
          stopTurnPoll(scid);
          loadedRef.current[scid] = false;   // снять guard → рефетч ленты
          const refetch = ensureMessages(scid); // готовый ответ всплывёт из БД
          if (st && st.status === 'done') noteReplyRef.current(scid);   // #503
          if (st && st.status === 'failed') {
            toast.show(st.error_detail || 'Не удалось сгенерировать ответ. Повторите.', 'danger');
            Promise.resolve(refetch).then(() => ensureFailedBubble(scid, st));
          }
        }
      }).catch(() => {
        turnTimersRef.current[scid] = setTimeout(() => pollTurn(scid), 3000);
      });
    }, [ensureMessages, ensureTurnSpinner, ensureFailedBubble, stopTurnPoll, toast]);

    // На открытии/refresh чата: если на беке активен ход (и живого SSE-стрима в
    // этом клиенте нет — иначе его ведёт runStream, не дублируем) → поднять
    // спиннер и поллить до финала.
    const checkInFlightTurn = useCallback((rawId) => {
      if (rawId == null) return;
      const scid = String(rawId);
      if (streaming[scid] || turnTimersRef.current[scid]) return;
      chatsApi.getTurnStatus(scid).then((r) => {
        const st = r && r.ok ? r.data : null;
        if (!st) return;
        if (st.active || st.status === 'running' || st.status === 'pending') {
          setTurns((m) => ({ ...m, [scid]: { active: true, status: st.status, error: '' } }));
          ensureTurnSpinner(scid);
          pollTurn(scid);
          return;
        }
        // F41: после reload показываем упавший ход (раньше он просто исчезал —
        // пользователь видел «завис» и писал «жду» в пустоту). Пузырь добавится,
        // только если лента реально кончается user-сообщением без ответа.
        if (st.status === 'failed') {
          Promise.resolve(ensureMessages(scid)).then(() => ensureFailedBubble(scid, st));
        }
      }).catch(() => {});
    }, [streaming, ensureTurnSpinner, ensureFailedBubble, ensureMessages, pollTurn]);

    // Стоп ТОЛЬКО выбранного чата: гасим SSE-контроллер локального стрима И
    // просим бэк остановить серверный ход (F38: abort SSE его НЕ прерывает) +
    // фоновую сборку дерева (F40). Остальные диалоги независимы.
    const cancel = useCallback((cid) => {
      const scid = String(cid);
      const fn = controllersRef.current[scid];
      if (fn) { fn(); delete controllersRef.current[scid]; }
      const aId = streaming[scid];
      if (aId) setChatMsgs(scid, (arr) => arr.map((m) => (m.id === aId ? { ...m, streaming: false } : m)));
      setStreaming((s) => { const n = { ...s }; delete n[scid]; return n; });
      // F38: реально гасим серверный ход (идемпотентно: нет хода → бэк вернёт idle).
      chatsApi.cancelTurn(scid).catch(() => {});
      const job = treeJobsRef.current[scid];
      if (job && job.active && job.jobId != null) api.treeBuildCancel(job.jobId).catch(() => {});
    }, [streaming]);

    const openChat = useCallback((c) => navigate('/chat/' + c.id), [navigate]);

    // Принять чат, созданный ВНЕ провайдера (напр., «AI-разбор» вкладки аналитики
    // создаёт диалог на бэке вместе с готовым ответом): нормализуем, кладём в
    // начало списка (без дублей) и открываем. loadedRef НЕ трогаем — сообщения
    // (запрос + заполненная форма разбора) подтянет ensureMessages при открытии.
    const adoptChat = useCallback((raw) => {
      if (!raw || raw.id == null) return null;
      const chat = normalizeChat(raw);
      setChats((l) => [chat, ...(l || []).filter((x) => x.id !== chat.id)]);
      navigate('/chat/' + chat.id);
      return chat.id;
    }, [navigate]);

    // Создать реальный чат (опц. скилл/spawn) → нормализованный объект или null.
    // 402 subscription_expired (S34): закрытый доступ к модели — не сбой, а ожидание тарифа:
    // причина сервера нейтральным тоном, без красного «Не удалось…» и без повтора (повтор
    // получил бы тот же отказ) — как у перегенерации и «Достроить» (kitchen-ux E1).
    const createRealChat = useCallback(async (skill = '', extra = {}) => {
      const r = await chatsApi.createChat(skill, extra);
      if (!r.ok || !r.data) {
        // 403 kitchen_edit_forbidden (pos-base-rework): чат-скилл ТТК/меню и чат-правка —
        // запись Продукции, право закрыто администратором. Причина сервера, не сбой.
        const forbidden = kitchenEditForbiddenDetail(r);
        const closed = forbidden || aiClosedDetail(r);
        if (closed) toast.show(closed, 'warning', 8000);
        else toast.show('Не удалось создать чат', 'danger');
        return null;
      }
      const chat = normalizeChat(r.data);
      loadedRef.current[chat.id] = true; // только что создан — не тянуть с сервера
      setChats((l) => [chat, ...(l || [])]);
      setMsgs((m) => ({ ...m, [chat.id]: [] }));
      return chat;
    }, [toast]);

    const newChat = useCallback(async () => {
      const c = await createRealChat('');
      if (c) navigate('/chat/' + c.id);
      return c ? c.id : null;
    }, [createRealChat, navigate]);

    const renameChat = useCallback(async (c, title) => {
      setChats((l) => (l || []).map((x) => (x.id === c.id ? { ...x, title } : x))); // оптимистично
      const r = await chatsApi.renameChat(c.id, title);
      if (!r.ok) { q.reload(); toast.show('Не удалось переименовать', 'danger'); }
    }, [q, toast]);

    const shareChat = useCallback(async (c, toShared) => {
      const visibility = toShared ? 'workspace' : 'private';
      setChats((l) => (l || []).map((x) => (x.id === c.id ? { ...x, visibility } : x)));
      // вкладка списка следует за чатом — иначе он «исчезает» с текущей вкладки
      setListTab(toShared ? 'workspace' : 'private');
      const r = await chatsApi.setChatVisibility(c.id, visibility);
      if (r.ok) toast.show(toShared ? 'Чат выведен в общие' : 'Чат возвращён в личные', 'success');
      else { q.reload(); setListTab(toShared ? 'private' : 'workspace'); toast.show('Не удалось изменить видимость', 'danger'); }
    }, [q, toast]);

    const deleteChat = useCallback(async (c, currentId) => {
      const ok = await confirm({ title: 'Удалить чат?', body: `Диалог «${c.title}» будет удалён без возможности восстановления.`, confirmLabel: 'Удалить', cancelLabel: 'Отмена', tone: 'danger' });
      if (!ok) return;
      setChats((l) => (l || []).filter((x) => x.id !== c.id));
      const r = await chatsApi.deleteChat(c.id);
      if (r.ok || r.status === 404) {
        if (currentId === c.id) navigate('/chat');
        toast.show('Чат удалён', 'muted');
      } else { q.reload(); toast.show('Не удалось удалить', 'danger'); }
    }, [confirm, navigate, toast, q]);

    // spawn дочернего чата для проработки полуфабриката (ttk-generate + spawn_for).
    const spawnChat = useCallback(async ({ skill = 'ttk-generate', spawnFor, ingredientIndex, parentTitle, prefill, title } = {}) => {
      // №523: Аргус закрыт участнику — чат разработки не заводим (как spawnEditChat)
      if (!canUseAssistant(activeWorkspace)) { toast.show(ASSISTANT_CLOSED_TEXT, 'muted'); return null; }
      const extra = spawnFor != null ? { spawn_for: { ttk_id: spawnFor, ingredient_index: ingredientIndex } } : {};
      const c = await createRealChat(skill, extra);
      if (!c) return null;
      if (title) { setChats((l) => (l || []).map((x) => (x.id === c.id ? { ...x, title } : x))); chatsApi.renameChat(c.id, title); }
      navigate('/chat/' + c.id + (prefill ? ('?prefill=' + encodeURIComponent(prefill)) : ''));
      toast.show('Создан чат для разработки полуфабриката', 'accent');
      return c.id;
    }, [createRealChat, navigate, toast, activeWorkspace]);

    // Новый чат, ПРИВЯЗАННЫЙ к существующей сущности кухни (кнопка «Редактировать
    // в чате» на ТТК/п-ф/сырье/меню). Бэк валидирует цель по workspace, кладёт
    // metadata.edit_target и сам именует чат («Правка: …»); шапка чата покажет
    // чип привязки, а модель получит блок «Сущность этого чата» в промпт.
    const spawnEditChat = useCallback(async ({ kind, id, title } = {}) => {
      if (!kind || id == null) return null;
      // №523: Аргус закрыт участнику — чат не заводим (единая точка всех «Редактировать в чате»)
      if (!canUseAssistant(activeWorkspace)) { toast.show(ASSISTANT_CLOSED_TEXT, 'muted'); return null; }
      const c = await createRealChat('', { edit_target: { kind, id: Number(id) } });
      if (!c) return null;
      navigate('/chat/' + c.id);
      toast.show(title ? `Чат привязан к «${title}» — опишите, что изменить` : 'Чат привязан к сущности', 'accent');
      return c.id;
    }, [createRealChat, navigate, toast, activeWorkspace]);

    // отправка + реальный стрим
    const send = useCallback(async (currentChatId, text, opts) => {
      // per-chat: для существующего чата ключ замка = его cid; для нового чата —
      // '__new__' (двойной сабмит в свежий /chat не плодит два чата). streaming[cid]
      // = чат уже стримит → второй ход в тот же чат не шлём.
      const lockKey = currentChatId != null ? String(currentChatId) : '__new__';
      if (sendingRef.current.has(lockKey)
          || (currentChatId != null && streaming[String(currentChatId)])) return;
      sendingRef.current.add(lockKey);  // синхронный замок до первого await
      try {
      opts = opts || {};
      let cid = currentChatId != null ? String(currentChatId) : null;
      if (!cid || !(chats || []).some((c) => c.id === cid)) {
        const c = await createRealChat('');
        if (!c) return;
        cid = c.id;
        navigate('/chat/' + cid);
      }
      const now = new Date().toISOString();
      const uId = 'u' + Date.now();
      const userMsg = {
        id: uId, role: 'user', content: text, time: now,
        attachments: (opts.attachments || []).map((a) => ({ id: a.id, name: a.name, size: a.size, mime: a.mime, url: a.url || '#' })),
        metadata: {}, toolCalls: [],
      };
      const aId = 'a' + Date.now();
      const aMsg = { id: aId, role: 'assistant', content: '', time: now, streaming: true, attachments: [], metadata: {}, toolCalls: [] };
      setChatMsgs(cid, (arr) => [...arr, userMsg, aMsg]);
      // chat-entities-edit: снимок живых несохранённых черновиков (с ручными правками) —
      // уходит в промпт, чтобы модель правила черновик. Отбрасываем committed/dismissed.
      const draft_context = [...liveDraftsRef.current.values()].filter(
        (d) => d && d.proposal_id && d.status !== 'committed' && d.status !== 'dismissed',
      );
      const skillSlug = ((chats || []).find((c) => c.id === cid) || {}).skill_slug || '';
      await runStream(cid, aId, uId, text, { ...opts, draft_context }, skillSlug);
      } finally {
        sendingRef.current.delete(lockKey);  // дальше двойной сабмит ловит streaming[cid]
      }
    }, [streaming, chats, createRealChat, navigate, runStream]);

    // перегенерация последнего ответа «на месте»: бэк удаляет хвостовой assistant
    // (+ гасит его pending-правки) и заново стримит в ТОТ ЖЕ пузырь. Регенерируем
    // только СОХРАНЁННЫЙ ответ ('m'+serverId) — временный 'a'+ts (ещё не done) нельзя.
    const regenerate = useCallback((cid, aMsg) => {
      if (streaming[String(cid)] || !aMsg) return;
      const match = /^m(\d+)$/.exec(aMsg.id || '');
      if (!match) return;
      const serverMsgId = match[1];
      const arr = msgsRef.current[cid] || [];
      const idx = arr.findIndex((m) => m.id === aMsg.id);
      let userMsgId = null;
      for (let i = idx - 1; i >= 0; i--) { if (arr[i].role === 'user') { userMsgId = arr[i].id; break; } }
      // снимок прежнего ответа — чтобы вернуть его на месте, если стрим упал
      // (бэк его тоже восстанавливает; здесь — чтобы не моргать пустым пузырём).
      const regenOriginal = { content: aMsg.content, toolCalls: aMsg.toolCalls || [], metadata: aMsg.metadata || {} };
      // сбрасываем пузырь на месте → новый ответ стримится в него же
      setChatMsgs(cid, (a) => a.map((m) => (m.id === aMsg.id
        ? { ...m, content: '', streaming: true, toolCalls: [], metadata: {} }
        : m)));
      const skillSlug = ((chats || []).find((c) => c.id === cid) || {}).skill_slug || '';
      runStream(cid, aMsg.id, userMsgId, '', { regenOriginal }, skillSlug, 'regenerate', serverMsgId);
    }, [streaming, chats, runStream]);

    // F41: повтор упавшего хода — стрим в существующий пузырь ошибки (msgId из
    // ленты или детерминированный 'turn-failed-<cid>' от ensureFailedBubble).
    const retryTurn = useCallback((cid, msgId) => {
      const scid = String(cid);
      if (streaming[scid]) return;
      const bubbleId = msgId || ('turn-failed-' + scid);
      setChatMsgs(scid, (arr) => arr.map((m) => (m.id === bubbleId
        ? { ...m, content: '', streaming: true, toolCalls: [], metadata: {} }
        : m)));
      const skillSlug = ((chats || []).find((c) => c.id === scid) || {}).skill_slug || '';
      const draft_context = [...liveDraftsRef.current.values()].filter(
        (d) => d && d.proposal_id && d.status !== 'committed' && d.status !== 'dismissed',
      );
      runStream(scid, bubbleId, null, '', { draft_context }, skillSlug, 'retry');
    }, [streaming, chats, runStream]);

    const value = {
      chats,
      loading: chats === null && q.loading,
      error: !!(q.error && q.error.status !== 403),
      forbidden: !!(q.error && q.error.status === 403),
      reload: q.reload,
      search, setSearch, listTab, setListTab, msgs,
      // ПОЧАТОВЫЕ аксессоры стрима (см. streaming-карту): busy/стрим-id выбранного
      // чата + cancel(cid) — независимые диалоги стримятся и останавливаются сами.
      // F40/F38: чат «занят», пока идёт live-стрим, фоновая сборка дерева
      // ИЛИ серверный ход (reconnect после refresh) — композер показывает «Стоп»,
      // а cancel(cid) гасит активное.
      isBusy: (cid) => {
        const scid = String(cid);
        const j = treeJobs[scid];
        const t = turns[scid];
        return !!streaming[scid] || !!(j && j.active) || !!(t && t.active);
      },
      streamingIdFor: (cid) => streaming[String(cid)] || null,
      ensureMessages,
      // #503: какой диалог на экране (ChatShell) и сколько диалогов с новым ответом
      setViewing,
      unreadCount: unreadCount(chats),
      // F38: reconnect к серверному ходу — ChatShell зовёт на открытии чата.
      checkInFlightTurn,
      openChat, adoptChat, newChat, renameChat, shareChat, deleteChat, send, spawnChat, spawnEditChat, cancel, regenerate,
      navByKind,
      // Право правки «Продукции» (kitchen.edit, pos-base-rework T9): false — закрыто
      // администратором (ChatShell прячет плитки-создатели, карточки и канвасы — кнопки
      // «Утвердить»); true — открыто или карты прав нет (личное пространство, статус не пришёл).
      canEditKitchen,
      // F41: повтор упавшего хода (кнопка «Повторить» в пузыре ошибки).
      retryTurn,
      // chat-2: inline-черновики + подготовленные правки (карточки feed зовут фасад через провайдер)
      replaceDraft, registerDraftLive, unregisterDraftLive,
      recomputeDraft: api.recomputeDraft, commitDraft: api.commitDraft,
      commitPendingChange: api.commitPendingChange, cancelPendingChange: api.cancelPendingChange,
      restorePendingChange: api.restorePendingChange,
      // F40 W5: фоновая сборка дерева (карточка чата зовёт через провайдер).
      startTreeFill, resumeTreeFill, clearTreeFill, resumeFailedTree, restartTreeBuild,
      treeFillFor: (cid) => treeJobs[String(cid)] || null,
    };
    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
  }

  export const useChat = () => useContext(ChatContext);

export { ChatProvider };
